/**
 * Signatures at L2 (IDEAS X1, the sector side): battles and movement leave traces on a system — who fought there and
 * how hard (what fired, from which lane side, how long it held), which lanes a faction's squads came in by, how long
 * a holder kept it. A trace fades by a half-life in days and is dropped once faint. A faction learns a system's
 * traces as it learns the system: by holding or watching it, scouting it, fighting there or hearing a loud battle
 * (knowledge.ts copies them into `KnownSystem.traces`). The stand-in reads them when picking where to go: fresh
 * enemy fighting there is danger (hops added), fresh enemy traffic through it is a lane worth cutting (hops taken
 * off). Behind `rules.signatures`. Pure.
 */
import type { BattleOutcome, BattleSetup, FactionId, SectorState, Squad, SystemId } from './types';
import { dpow } from '../core/dmath';

export type TraceKind = 'fought' | 'passed' | 'held';

export interface Trace {
  systemId: SystemId;
  faction: FactionId;
  kind: TraceKind;
  day: number;
  /** Strength when laid, 0–1. */
  weight: number;
  /** The lane side it came in by (`passed`, and `fought` for an attacker): the neighbour system. */
  from?: SystemId;
  detail: string;
}

/** Days for a trace to fade to half. */
export const TRACE_HALF_LIFE_DAYS = 6;
/** Below this strength a trace is gone. */
export const TRACE_MIN = 0.05;
/** The stand-in: fresh enemy fighting at a system costs this many hops of closeness per unit of strength … */
export const TRACE_DANGER_HOPS = 1.5;
/** … and fresh enemy traffic through it buys this many. */
export const TRACE_LANE_HOPS = 1.0;

/** A trace's strength today. */
export function traceStrength(t: Pick<Trace, 'day' | 'weight'>, day: number): number {
  return t.weight * dpow(0.5, Math.max(0, day - t.day) / TRACE_HALF_LIFE_DAYS);
}

/** Lay traces (the rule off: nothing). */
export function addTraces(state: SectorState, traces: Trace[]): SectorState {
  if (!state.rules.signatures || !traces.length) return state;
  return { ...state, traces: [...(state.traces ?? []), ...traces] };
}

/** Drop what has faded. */
export function pruneTraces(state: SectorState): SectorState {
  if (!state.traces?.length) return state;
  const kept = state.traces.filter(t => traceStrength(t, state.day) >= TRACE_MIN);
  return kept.length === state.traces.length ? state : { ...state, traces: kept };
}

/** The traces a system carries now, strongest first. */
export function tracesAt(state: Pick<SectorState, 'traces' | 'day'>, systemId: SystemId): Trace[] {
  return (state.traces ?? []).filter(t => t.systemId === systemId && traceStrength(t, state.day) >= TRACE_MIN)
    .sort((a, b) => traceStrength(b, state.day) - traceStrength(a, state.day));
}

/** What a battle leaves: one `fought` trace per side — its shots, the lane side it came by, how long it held. */
export function battleTraces(state: SectorState, setup: BattleSetup, outcome: BattleOutcome): Trace[] {
  const sysName = (id: SystemId | undefined) => (id ? state.systems.find(s => s.id === id)?.name ?? id : undefined);
  const length = outcome.clock === 'action' ? `${Math.round(outcome.seconds ?? 0)} s` : `${outcome.rounds} round${outcome.rounds === 1 ? '' : 's'}`;
  const ids = new Map(setup.sides.map(s => [s.faction, new Set(s.units.map(u => u.unitId))] as const));
  return setup.sides.map(side => {
    const mine = outcome.units.filter(u => ids.get(side.faction)?.has(u.unitId));
    const shots = mine.reduce((a, u) => a + u.shots, 0);
    const alive = mine.filter(u => u.alive).length;
    // the lane this side came by: the squad's last arrival (an attacker); a holder has none
    const from = state.squads.find(q => side.squadIds.includes(q.id) && q.arrivedFrom)?.arrivedFrom ?? undefined;
    const won = outcome.winner === side.faction;
    const weight = Math.min(1, 0.4 + shots / 40 + (won ? 0.2 : 0));
    const detail = `${side.faction} fired ${shots} round${shots === 1 ? '' : 's'}${from ? ` from the ${sysName(from)} side` : ''}, ${won ? 'held' : 'stood'} ${length}, ${alive}/${mine.length} left`;
    return { systemId: setup.systemId, faction: side.faction, kind: 'fought', day: state.day, weight, ...(from ? { from } : {}), detail };
  });
}

/** What an arrival leaves: the lane the squad came in by. A sneaking squad leaves half the trace. */
export function arrivalTrace(state: SectorState, q: Squad, here: SystemId, from: SystemId): Trace {
  const fromName = state.systems.find(s => s.id === from)?.name ?? from;
  return { systemId: here, faction: q.faction, kind: 'passed', day: state.day, weight: q.stance === 'sneak' ? 0.25 : 0.5, from, detail: `${q.faction} came in by the ${fromName} lane` };
}

/** What a change of hands leaves: how long the last holder kept it. */
export function heldTrace(state: SectorState, systemId: SystemId, holder: FactionId, heldDays: number): Trace {
  return { systemId, faction: holder, kind: 'held', day: state.day, weight: Math.min(1, 0.3 + heldDays / 20), detail: `${holder} held it ${heldDays.toFixed(0)} day${Math.round(heldDays) === 1 ? '' : 's'}` };
}

/** The traces as lines for a panel, strongest first, at most `n`: strength as a word, then the detail. */
export function traceLines(traces: Trace[], day: number, n = 6): string[] {
  return traces.slice(0, n).map(t => {
    const s = traceStrength(t, day);
    const age = day - t.day;
    return `${s >= 0.6 ? 'fresh' : s >= 0.25 ? 'fading' : 'faint'} · ${t.detail} (${age < 1 ? 'today' : `${age.toFixed(0)} days ago`})`;
  });
}

/** What the stand-in reads: the enemy's fighting and traffic at a system, by strength today. */
export function enemyTraceScore(traces: readonly Trace[] | undefined, faction: FactionId, day: number): { fought: number; passed: number } {
  let fought = 0; let passed = 0;
  for (const t of traces ?? []) {
    if (t.faction === faction) continue;
    const s = traceStrength(t, day);
    if (s < TRACE_MIN) continue;
    if (t.kind === 'fought') fought += s; else if (t.kind === 'passed') passed += s;
  }
  return { fought, passed };
}

/** Hops of closeness the traces add (danger) or take off (a lane worth cutting) for the stand-in. */
export function traceHops(traces: readonly Trace[] | undefined, faction: FactionId, day: number): number {
  const e = enemyTraceScore(traces, faction, day);
  return e.fought * TRACE_DANGER_HOPS - e.passed * TRACE_LANE_HOPS;
}
